import { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldAlert } from 'lucide-react';
import { styles } from './EditCasesModal';

/**
 * Layer 3 flag detail modal. Shows Claude's extracted findings and
 * reasoning. Read-only for the assistant; advisor sees a "Mark as
 * verified" section that clears the flag with a reason.
 */
export default function FlagDetailModal({ shot, caseLabel, isAdviser, actionLoading, onClose, onClearFlag }) {
  const [reason, setReason] = useState('');
  const details = shot.verificationDetails || {};
  const trimmed = reason.trim();

  return (
    <div style={styles.modalOverlay} onClick={() => !actionLoading && onClose()}>
      <motion.div
        className="card"
        style={{ ...styles.modal, maxWidth: '520px' }}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <h3 style={{ marginBottom: '0.5rem', color: '#8C5A2D' }}>
          <ShieldAlert size={16} style={{ verticalAlign: '-3px', marginRight: '0.4rem' }} />
          Verification flagged
        </h3>
        <p style={{ color: 'var(--cp-text-muted)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
          This screenshot was saved but flagged because its content may not match
          the case label. The advisor can review and clear the flag if the
          screenshot is correct.
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <img
            src={shot.screenshotUrl}
            alt={`Case ${shot.caseIndex + 1} screenshot`}
            style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--cp-border)' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.82rem', flex: 1 }}>
            <div><strong>Case label:</strong> {caseLabel}</div>
            <div><strong>Extracted client:</strong> {details.extracted_client_name || '—'}</div>
            <div><strong>Extracted product house:</strong> {details.extracted_product_house || '—'}</div>
            <div><strong>Extracted transaction type:</strong> {details.extracted_transaction_type || '—'}</div>
          </div>
        </div>

        {details.case_match_reasoning && (
          <div style={{
            padding: '0.6rem 0.75rem',
            background: '#F7F9FC',
            border: '1px solid var(--cp-surface-alt)',
            borderRadius: '8px',
            fontSize: '0.82rem',
            color: 'var(--cp-text)',
            marginBottom: '0.75rem',
          }}>
            <strong>Reviewer reasoning:</strong> {details.case_match_reasoning}
          </div>
        )}

        {isAdviser ? (
          <div style={{
            paddingTop: '0.75rem',
            borderTop: '1px solid var(--cp-surface-alt)',
            display: 'flex', flexDirection: 'column', gap: '0.5rem',
          }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--cp-text)' }}>
              Reason for clearing this flag
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Confirmed with adviser — screenshot is correct."
              rows={2}
              disabled={actionLoading}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-outline" onClick={onClose} disabled={actionLoading}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => onClearFlag(trimmed)}
                disabled={!trimmed || actionLoading}
              >
                {actionLoading ? 'Clearing…' : 'Mark as verified'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-outline" onClick={onClose}>Close</button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
